import type { AuthRequest, AuthResponse } from '@/types';

export type AuthPayload = Required<Pick<AuthRequest, 'email' | 'password'>> & {
  full_name?: string | null;
};

const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/api/v1`;

function logAuthInfo(message: string, details: Record<string, unknown>) {
  console.info(`[auth] ${message}`, details);
}

function logAuthError(message: string, details: Record<string, unknown>) {
  console.error(`[auth] ${message}`, details);
}

async function handleAuthResponse(
  response: Response,
  action: 'login' | 'signup',
  email: string
): Promise<AuthResponse> {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (payload as { detail?: string; message?: string })?.detail ||
      (payload as { detail?: string; message?: string })?.message ||
      'Request failed. Please try again.';
    logAuthError(`${action} response error`, {
      email,
      status: response.status,
      message,
    });
    throw new Error(message);
  }
  logAuthInfo(`${action} response success`, { email, status: response.status });
  return payload as AuthResponse;
}

export async function login(payload: AuthPayload): Promise<AuthResponse> {
  const url = `${API_BASE_URL}/auth/login`;
  logAuthInfo('login request', { email: payload.email, url });
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: payload.email, password: payload.password }),
    });
    return handleAuthResponse(response, 'login', payload.email ?? "");
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    logAuthError('login request failed', { email: payload.email, message });
    throw err;
  }
}

export async function signup(payload: AuthPayload): Promise<AuthResponse> {
  const url = `${API_BASE_URL}/auth/signup`;
  logAuthInfo('signup request', { email: payload.email, url });
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: payload.email,
        password: payload.password,
        full_name: payload.full_name,
      }),
    });
    return handleAuthResponse(response, 'signup', payload.email ?? "");
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    logAuthError('signup request failed', { email: payload.email, message });
    throw err;
  }
}

export async function loginWithGoogle(idToken: string): Promise<AuthResponse> {
  const url = `${API_BASE_URL}/auth/google`;
  logAuthInfo('google login request', { url });
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken }),
    });
    return handleAuthResponse(response, 'login', 'google');
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    logAuthError('google login request failed', { message });
    throw err;
  }
}
